import { v4 as uuidv4 } from "uuid";
import { CommonNS } from "../../../../../../Common/CommonNS.js";
import { PathEditorNS } from "../../../../../../PathEditor/PathEditorNS.js";
import { StrokeNS } from "../../../../../../Stroke/StrokeNS.js";
import { ViewBoxNS } from "../../../../../../ViewBox/ViewBoxNS.js";
import { ViewPortNS } from "../../../../../../ViewPort/ViewPortNS.js";
import { BaseNS } from "../../Base/BaseNS.js";
import { _handleMouseDown } from "../private/_handleMouseDown.js";
import { _handleMouseMove } from "../private/_handleMouseMove.js";
import { _handleMouseUp } from "../private/_handleMouseUp.js";
import { calculateViewBoxFitTo } from "../private/calculateViewBoxFitTo.js";
import { destroy } from "../private/destroy.js";
import { initEditor } from "../private/initEditor.js";
export class AcrylicCuttingLinePCEditor {
  constructor({ window, document, min_zoom_scale, max_zoom_scale }) {
    this._window = window;
    this._document = document;
    this._svg_el = document.createElementNS(
      CommonNS.ConstNS.SVG_NAMESPACE,
      "svg"
    );
    this._min_zoom_scale = min_zoom_scale;
    this._max_zoom_scale = max_zoom_scale;
    this._outer_cutting_line_clip_path_id = `${
      CommonNS.ConstNS.DATA_KEY_ROLE
    }__outer-cutting-line-clip-path__${uuidv4()}`;
    this._inner_cutting_line_clip_path_id = `${
      CommonNS.ConstNS.DATA_KEY_ROLE
    }__inner-cutting-line-clip-path__${uuidv4()}`;
    this._outer_cutting_line_path_data = "M 0 0";
    this._inner_cutting_line_path_data = "M 0 0";
    this._container_el = null;
    this._canvas_el = null;
    this._dummy_g_el = null;
    this._layers_el = null;
    this._target_image_layer_el = null;
    this._cutting_line_layer_el = null;
    this._path_editor_layer_el = null;
    this._target_image_outer_wrapper_g_el = null;
    this._target_image_inner_wrapper_g_el = null;
    this._target_image_dummy_g_el = null;
    this._outer_cutting_line_clip_path_el = null;
    this._inner_cutting_line_clip_path_el = null;
    this._outer_cutting_line_display_path_el = null;
    this._path_editor_line_container_el = null;
    this._path_editor_point_container_el = null;
    this._view_port = null;
    this._view_box = null;
    this._stroke_sync = null;
    this._target_image_el = null;
    this._path_editor_list = [];
    this._path_editor_point_size_controller = null;
    this._processing_task = null;
    this._mouseDownHandler = null;
    this._mouseMoveHandler = null;
    this._mouseUpHandler = null;
    this._is_mouse_down = false;
    this._is_mouse_move = false;
    this._mouse_down_position = {
      x: 0,
      y: 0,
    };
    this._mouse_move_position = {
      x: 0,
      y: 0,
    };
  }
  initDOM() {
    return BaseNS.initDOM(this);
  }
  initEditor() {
    return initEditor(this);
  }
  destroy() {
    return destroy(this);
  }
  appendTo(container_el) {
    return BaseNS.appendTo(this, container_el);
  }
  fitSize() {
    return BaseNS.fitSize(this);
  }
  setTargetImageEl(target_image_el) {
    return BaseNS.setTargetImageEl(this, target_image_el);
  }
  calculateViewBoxFitTo(config) {
    return calculateViewBoxFitTo(this, config);
  }
  setViewBox(view_box_vo) {
    return BaseNS.setViewBox(this, view_box_vo);
  }
  getOuterCuttingLinePathEl() {
    return BaseNS.getOuterCuttingLinePathEl(this);
  }
  getOuterCuttingLinePathData() {
    return BaseNS.getOuterCuttingLinePathData(this);
  }
  setOuterCuttingLinePathData(path_data) {
    return BaseNS.setOuterCuttingLinePathData(this, path_data);
  }
  getInnerCuttingLinePathData() {
    return BaseNS.getInnerCuttingLinePathData(this);
  }
  setInnerCuttingLinePathData(path_data) {
    return BaseNS.setInnerCuttingLinePathData(this, path_data);
  }
  connectOuterCuttingLineClipPath() {
    return BaseNS.connectOuterCuttingLineClipPath(this);
  }
  disconnectOuterCuttingLineClipPath() {
    return BaseNS.disconnectOuterCuttingLineClipPath(this);
  }
  connectInnerCuttingLineClipPath() {
    return BaseNS.connectInnerCuttingLineClipPath(this);
  }
  disconnectInnerCuttingLineClipPath() {
    return BaseNS.disconnectInnerCuttingLineClipPath(this);
  }
  startEditCuttingLinePath() {
    return BaseNS.startEditCuttingLinePath(this);
  }
  getActiveCuttingLinePathCommand() {
    return BaseNS.getActiveCuttingLinePathCommand(this);
  }
  getPrevCuttingLinePathCommand(ref_command) {
    return BaseNS.getPrevCuttingLinePathCommand(this, ref_command);
  }
  getNextCuttingLinePathCommand(ref_command) {
    return BaseNS.getNextCuttingLinePathCommand(this, ref_command);
  }
  insertCuttingLinePathEditorBefore({ ref_command, makePathEditor }) {
    return BaseNS.insertCuttingLinePathEditorBefore(this, {
      ref_command,
      makePathEditor,
    });
  }
  replaceCuttingLinePathEditorCommand({ ref_command, makePathEditor }) {
    return BaseNS.replaceCuttingLinePathEditorCommand(this, {
      ref_command,
      makePathEditor,
    });
  }
  removeCuttingLinePathCommand(command) {
    return BaseNS.removeCuttingLinePathCommand(this, command);
  }
  endEditCuttingLinePath() {
    return BaseNS.endEditCuttingLinePath(this);
  }
  toViewPortPoint(point) {
    return BaseNS.toViewPortPoint(this, point);
  }
  toUserPoint(view_port_point) {
    return BaseNS.toUserPoint(this, view_port_point);
  }
  convertCoordsToOutside(payload) {
    return BaseNS.convertCoordsToOutside(this, payload);
  }
  _getContainerElThrowable() {
    return BaseNS._getContainerElThrowable(this);
  }
  _getCanvasElThrowable() {
    return BaseNS._getCanvasElThrowable(this);
  }
  _getDummyGElThrowable() {
    return BaseNS._getDummyGElThrowable(this);
  }
  _getLayersElThrowable() {
    return BaseNS._getLayersElThrowable(this);
  }
  _getTargetImageLayerElThrowable() {
    return BaseNS._getTargetImageLayerElThrowable(this);
  }
  _getCuttingLineLayerElThrowable() {
    return BaseNS._getCuttingLineLayerElThrowable(this);
  }
  _getPathEditorLayerElThrowable() {
    return BaseNS._getPathEditorLayerElThrowable(this);
  }
  _getTargetImageOuterWrapperGElThrowable() {
    return BaseNS._getTargetImageOuterWrapperGElThrowable(this);
  }
  _getTargetImageInnerWrapperGElThrowable() {
    return BaseNS._getTargetImageInnerWrapperGElThrowable(this);
  }
  _getTargetImageDummyGElThrowable() {
    return BaseNS._getTargetImageDummyGElThrowable(this);
  }
  _getOuterCuttingLineClipPathElThrowable() {
    return BaseNS._getOuterCuttingLineClipPathElThrowable(this);
  }
  _getInnerCuttingLineClipPathElThrowable() {
    return BaseNS._getInnerCuttingLineClipPathElThrowable(this);
  }
  _getOuterCuttingLineDisplayPathElThrowable() {
    return BaseNS._getOuterCuttingLineDisplayPathElThrowable(this);
  }
  _getPathEditorLineContainerElThrowable() {
    return BaseNS._getPathEditorLineContainerElThrowable(this);
  }
  _getPathEditorPointContainerElThrowable() {
    return BaseNS._getPathEditorPointContainerElThrowable(this);
  }
  _getViewPortThrowable() {
    return BaseNS._getViewPortThrowable(this);
  }
  _getViewBoxThrowable() {
    return BaseNS._getViewBoxThrowable(this);
  }
  _getStrokeSyncThrowable() {
    return BaseNS._getStrokeSyncThrowable(this);
  }
  _getPathEditorPointSizeControllerThrowable() {
    return BaseNS._getPathEditorPointSizeControllerThrowable(this);
  }
  _checkIsPathEditing() {
    return BaseNS._checkIsPathEditing(this);
  }
  _checkNoProcessingTask() {
    return BaseNS._checkNoProcessingTask(this);
  }
  _handleMouseDown(event) {
    return _handleMouseDown(this, event);
  }
  _handleMouseMove(event) {
    return _handleMouseMove(this, event);
  }
  _handleMouseUp(event) {
    return _handleMouseUp(this, event);
  }
}
